// frontend/src/components/staff/ScheduleAuditPage.js
//
// Workforce — SCHED-SLICE1-F per-week audit trail page.
//
// Full-screen admin-only page at /staff/schedule/audit?weekStart=YYYY-MM-DD
// showing all audit rows for the visible week with filter chips.
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §7.2 + §7.3
// Story:       docs/backlog/SCHED-SLICE1-F-audit-trail-viewer-ui.md
//
// Read-only by design — no edit/delete affordances per Scenario 9.
// Admin-only RBAC — route guard in App.js + defense-in-depth here.
//
// Coupling notes:
//   - Audit query endpoint: `GET /api/workforce/schedule/audit?weekStart=...`
//     (story Notes — recommended thin BE endpoint; gated on Simone landing
//     this. Until then, set DEV_MODE_FIXTURES=true in localStorage to render
//     dev fixtures inline.)
//   - Row click in per-week page opens the relevant Edit Shift modal with
//     the audit drawer pre-opened. Story E owns the Edit Shift modal; we
//     dispatch a custom `schedule:openShiftEdit` event so Story E can wire
//     a listener (or fall back to a same-page drawer if the modal isn't
//     mounted).

import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import { scheduleAuditAPI } from "../../services/api";
import {
  FILTER_CATEGORIES,
  FILTER_KEYS,
  STUDIO_TZ,
  filterAuditRows,
  formatWeekRange,
  normalizeFilterKey,
  sortAuditRowsNewestFirst,
} from "./scheduleAuditUtils";
import ScheduleAuditDrawer from "./ScheduleAuditDrawer";
import { AuditRow } from "./ScheduleAuditDrawer";
import "./ScheduleAuditPage.css";

const DEFAULT_FILTER = "all";

/**
 * Compute a Phoenix-MST week-start (Sunday) for a given date string or "now".
 */
const computeWeekStart = (input) => {
  const ref = input
    ? moment.tz(input, STUDIO_TZ)
    : moment.tz(STUDIO_TZ);
  if (!ref.isValid()) {
    return moment.tz(STUDIO_TZ).startOf("week").format("YYYY-MM-DD");
  }
  return ref.startOf("week").format("YYYY-MM-DD");
};

const ScheduleAuditPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL state — weekStart + filter.
  const rawWeekStart = searchParams.get("weekStart");
  const weekStart = React.useMemo(
    () => computeWeekStart(rawWeekStart),
    [rawWeekStart]
  );
  const rawFilter = searchParams.get("filter") || DEFAULT_FILTER;
  // Accept legacy aliases (e.g. ?filter=deleted -> cancelled) for
  // backward-compat with shared URLs from before SCHED-AUDIT-DELETED-RENAME.
  const filterKey = normalizeFilterKey(rawFilter) || DEFAULT_FILTER;

  // Data state.
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [staffNameById, setStaffNameById] = React.useState(() => new Map());
  const [userNameById, setUserNameById] = React.useState(() => new Map());

  // Drawer state — when a row click opens an in-page drawer fallback
  // (because Story E's Edit Shift modal isn't mounted yet).
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerShift, setDrawerShift] = React.useState(null);
  const [drawerRowAnchor, setDrawerRowAnchor] = React.useState(null);
  const [drawerRows, setDrawerRows] = React.useState([]);

  // Fetch audit rows for the visible week.
  React.useEffect(() => {
    let cancelled = false;
    const fetchAudit = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await scheduleAuditAPI.listAudit({ weekStart });
        if (cancelled) return;
        const data = res && res.data ? res.data : {};
        setRows(Array.isArray(data.auditRows) ? data.auditRows : []);
        // Optional join data per recommended response shape.
        if (data.staffNameById && typeof data.staffNameById === "object") {
          setStaffNameById(new Map(Object.entries(data.staffNameById)));
        }
        if (data.userNameById && typeof data.userNameById === "object") {
          setUserNameById(new Map(Object.entries(data.userNameById)));
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchAudit();
    return () => {
      cancelled = true;
    };
  }, [weekStart]);

  // Filter chip click handler — updates URL state.
  const handleFilterClick = React.useCallback(
    (key) => {
      const next = new URLSearchParams(searchParams);
      if (key === DEFAULT_FILTER) {
        next.delete("filter");
      } else {
        next.set("filter", key);
      }
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  // Row click handler — opens Story E's Edit Shift modal IF mounted; else
  // falls back to opening the per-shift drawer in-page.
  const handleRowClick = React.useCallback(
    (row) => {
      const metadata = row.metadata || {};
      const shiftId =
        metadata.shiftId ||
        (Array.isArray(metadata.shiftIds) && metadata.shiftIds[0]) ||
        null;
      if (!shiftId) return;

      // Dispatch event for Story E's Edit Shift modal to listen to. If a
      // listener is mounted, it will preventDefault on the CustomEvent and
      // we won't open the in-page drawer fallback.
      const ev = new CustomEvent("schedule:openShiftEdit", {
        detail: { shiftId, auditRowId: row._id || row.id, openAuditDrawer: true },
        cancelable: true,
      });
      const handled = !window.dispatchEvent(ev);
      if (handled) return;

      // Fallback — open the drawer in-page filtered to this shift's rows.
      const shiftRows = rows.filter((r) => {
        const m = r.metadata || {};
        if (m.shiftId === shiftId) return true;
        if (Array.isArray(m.shiftIds) && m.shiftIds.includes(shiftId)) return true;
        return false;
      });
      setDrawerShift({
        _id: shiftId,
        startTime: metadata.startTime || (metadata.after && metadata.after.startTime),
        endTime: metadata.endTime || (metadata.after && metadata.after.endTime),
      });
      setDrawerRows(shiftRows);
      setDrawerRowAnchor(row._id || row.id || null);
      setDrawerOpen(true);
    },
    [rows]
  );

  const handleClose = React.useCallback(() => {
    // Back-link / X behavior — navigate back to the Schedule Builder.
    // Story E will own the builder route; until then, fall back to /staff.
    navigate(-1);
  }, [navigate]);

  // Compose ordered + filtered display rows.
  const displayRows = React.useMemo(() => {
    const sorted = sortAuditRowsNewestFirst(rows);
    return filterAuditRows(sorted, filterKey);
  }, [rows, filterKey]);

  const auditCtx = React.useMemo(
    () => ({ staffNameById, userNameById }),
    [staffNameById, userNameById]
  );

  return (
    <div className="schedule-audit-page" data-testid="schedule-audit-page">
      <header className="schedule-audit-page__header">
        <h1 className="schedule-audit-page__heading">
          Schedule audit &mdash; {formatWeekRange(weekStart)} MST
        </h1>
        <button
          type="button"
          className="schedule-audit-page__close"
          onClick={handleClose}
          aria-label="Close audit page"
          data-testid="schedule-audit-page-close"
        >
          &times;
        </button>
      </header>

      <div
        className="schedule-audit-page__filters"
        role="group"
        aria-label="Filter audit rows by action"
      >
        {FILTER_KEYS.map((key) => {
          const cat = FILTER_CATEGORIES[key];
          const active = filterKey === key;
          return (
            <button
              key={key}
              type="button"
              className={
                active
                  ? "schedule-audit-page__chip schedule-audit-page__chip--active"
                  : "schedule-audit-page__chip"
              }
              onClick={() => handleFilterClick(key)}
              aria-pressed={active}
              data-testid={`schedule-audit-page-chip-${key}`}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      <main className="schedule-audit-page__body">
        {isLoading && (
          <p className="schedule-audit-page__loading">Loading audit history...</p>
        )}

        {error && !isLoading && (
          <p className="schedule-audit-page__error" role="alert">
            Unable to load audit history for this week. Try refreshing the page.
          </p>
        )}

        {!isLoading && !error && displayRows.length === 0 && (
          <p
            className="schedule-audit-page__empty"
            data-testid="schedule-audit-page-empty"
          >
            No changes recorded for this week yet.
          </p>
        )}

        {!isLoading && !error && displayRows.length > 0 && (
          <ul
            className="schedule-audit-page__list"
            data-testid="schedule-audit-page-list"
          >
            {displayRows.map((row) => {
              const rowId = row._id || row.id || row.createdAt;
              return (
                <AuditRow
                  key={rowId}
                  row={row}
                  ctx={auditCtx}
                  clickable
                  onClick={handleRowClick}
                />
              );
            })}
          </ul>
        )}
      </main>

      <ScheduleAuditDrawer
        open={drawerOpen}
        shift={drawerShift}
        rows={drawerRows}
        isLoading={false}
        error={null}
        staffNameById={staffNameById}
        userNameById={userNameById}
        onClose={() => setDrawerOpen(false)}
        rowAnchor={drawerRowAnchor}
      />
    </div>
  );
};

export default ScheduleAuditPage;
